import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { useDebounced } from '../../lib/useDebounced';
import { useContactLookup } from './queries';

interface EmailComboboxProps {
  id?: string;
  ariaLabel?: string;
  /** Die gewaehlte oder eingegebene Adresse. */
  value: string;
  onChange: (email: string) => void;
  /** Weitere Eintraege, die lokal mitgefiltert werden -- etwa Bearbeiter. */
  extraOptions?: ComboboxOption[];
  /** Adressen, die schon gewaehlt sind und nicht noch einmal erscheinen. */
  excludeEmails?: string[];
  placeholder?: string;
  disabled?: boolean;
  /**
   * Einzelne Adresse (Vorgabe): Das Feld zeigt sie, und getippter Text gilt
   * sofort als Wert. Liste: Das Feld leert sich nach jeder Wahl, der Aufrufer
   * fuehrt die gewaehlten Adressen selbst -- und bekommt den noch nicht
   * uebernommenen Text ueber ``onPending``.
   */
  keepValue?: boolean;
  onPending?: (text: string) => void;
}

/**
 * Eine E-Mail-Adresse waehlen -- aus dem CRM oder frei eingegeben.
 *
 * Vorgeschlagen werden Ansprechpartner aller Kunden mit Name, Adresse und
 * Kunde. **Eine Adresse, die nicht im CRM steht, bleibt zulaessig:** Eine
 * betroffene Person nach DSGVO ist nicht immer ein Kontakt, und ein Bericht
 * geht auch an die Geschaeftsfuehrung, die nirgends als Ansprechpartner
 * gefuehrt wird. Die Eingabetaste uebernimmt dann den getippten Text.
 *
 * Ohne das Recht ``crm.read`` bleibt die Liste leer und das Feld ein
 * gewoehnliches Eingabefeld -- es scheitert nicht.
 */
export function EmailCombobox({
  id,
  ariaLabel,
  value,
  onChange,
  extraOptions = [],
  excludeEmails = [],
  placeholder,
  disabled,
  keepValue = true,
  onPending,
}: EmailComboboxProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  // Erst beim ersten Oeffnen fragen -- siehe ``useCustomerLookup``.
  const [opened, setOpened] = useState(false);
  const search = useDebounced(term);
  const contacts = useContactLookup(search, opened);

  const needle = term.trim().toLowerCase();
  const excluded = excludeEmails.map((email) => email.toLowerCase());

  const fromExtra = extraOptions.filter(
    (option) =>
      needle === '' ||
      option.label.toLowerCase().includes(needle) ||
      option.id.toLowerCase().includes(needle),
  );
  const fromContacts: ComboboxOption[] = (contacts.data ?? [])
    .filter((contact) => contact.email)
    .map((contact) => ({
      id: contact.email ?? '',
      label: `${contact.first_name} ${contact.last_name}`.trim(),
      hint: contact.email ?? undefined,
      meta: contact.customer_name,
    }));

  const seen = new Set<string>();
  const options = [...fromExtra, ...fromContacts].filter((option) => {
    const key = option.id.toLowerCase();
    if (seen.has(key) || excluded.includes(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <Combobox
      id={id}
      ariaLabel={ariaLabel}
      value={keepValue && value ? { id: value, label: value } : null}
      onChange={(option) => {
        // Die Kennung eines Eintrags ist seine Adresse; frei eingegeben steht
        // sie im Text.
        onChange(option ? option.id || option.label : '');
        onPending?.('');
      }}
      options={options}
      onSearch={(next) => {
        setOpened(true);
        setTerm(next);
      }}
      onInput={(text) => {
        if (keepValue) onChange(text.trim());
        else onPending?.(text.trim());
      }}
      loading={contacts.isFetching}
      placeholder={placeholder ?? t('combobox.searchEmail')}
      disabled={disabled}
      allowFreeText
    />
  );
}
